/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';

import NoImageIcon from './NoImageIcon';
import css from './ResponsiveImage.module.css';

/**
 * Responsive image
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.alt alt attribute for the img element
 * @param {Object?} props.image API entity (image or imageAsset)
 * @param {Array<string>} props.variants
 * @param {string?} props.sizes sizes attribute for the img element (to be used with srcset)
 * @param {string?} props.noImageMessage message to be shown, when no image was given
 * @returns {JSX.Element} responsive image
 */
const ResponsiveImage = props => {
  const {
    className,
    rootClassName,
    alt,
    noImageMessage,
    image,
    variants,
    dimensions,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  if (image == null || variants.length === 0) {
    const noImageClasses = classNames(rootClassName || css.root, css.noImageContainer, className);

    const noImageMessageText = noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;
    return (
      <div className={noImageClasses}>
        <div className={css.noImageWrapper}>
          <NoImageIcon className={css.noImageIcon} />
          <div className={css.noImageText}>{noImageMessageText}</div>
        </div>
      </div>
    );
  }

  const imageVariants = image.attributes.variants;

  // Authentication handling for Sharetribe images (SSR safe)
  const isBrowser = typeof window !== 'undefined' && typeof window.store !== 'undefined';
  const authInfo = isBrowser && typeof window.store.getState === 'function' ? window.store.getState().auth : null;
  const isAuthenticated = !!(authInfo && !authInfo.isAnonymous);
  
  // Helper function to check if URL needs authentication
  const needsAuth = (url) => {
    return url && /^https:\/\/flex-api\.sharetribe\.com.*\/assets\//i.test(url);
  };
  
  // Helper function to get authenticated URL
  const getAuthUrl = (url) => {
    if (!needsAuth(url)) return url;
    // On the server, don't attempt to hit relative /api proxy. Let server render the public URL; client will re-hydrate with auth URL if needed.
    if (typeof window === 'undefined') return url;
    if (!isAuthenticated) return null; // Skip unauthenticated requests
    const apiBase = process.env.NODE_ENV === 'development' 
      ? `http://localhost:${process.env.REACT_APP_DEV_API_SERVER_PORT || 3500}`
      : '';
    return `${apiBase}/api/authenticated-image?url=${encodeURIComponent(url)}`;
  };

  let srcSet = variants
    .map(variantName => {
      const variant = imageVariants[variantName];

      if (!variant) {
        return null;
      }
      
      // Use authenticated URL if needed
      let imageUrl = variant.url;
      if (needsAuth(imageUrl)) {
        const authUrl = getAuthUrl(imageUrl);
        if (!authUrl) return null; // Skip if not authenticated
        imageUrl = authUrl;
      }

      return `${imageUrl} ${variant.width}w`;
    })
    .filter(v => v != null)
    .join(', ');

  // If auth filtering removed all srcset entries, rebuild without auth to keep SSR rendering consistent
  if (!srcSet) {
    srcSet = variants
      .map(variantName => {
        const variant = imageVariants[variantName];
        return variant ? `${variant.url} ${variant.width}w` : null;
      })
      .filter(v => v != null)
      .join(', ');
  }

  // Get the first variant for the src attribute
  const firstVariant = variants.find(variantName => imageVariants[variantName]);
  let srcUrl = '';
  if (firstVariant && imageVariants[firstVariant]) {
    srcUrl = imageVariants[firstVariant].url;
    // Apply authentication if needed; keep SSR safe by not rewriting on server
    if (needsAuth(srcUrl)) {
      const authUrl = getAuthUrl(srcUrl);
      srcUrl = authUrl || srcUrl; // Fall back to original if auth fails or on server
    }
  }

  const imgProps = {
    className: classes,
    src: srcUrl,
    srcSet,
    ...rest,
  };

  return <img alt={alt} {...imgProps} />;
};

export default ResponsiveImage;
